/**
 * Customer Store Actions - Internal
 */

import { customerStore$ } from './customer.store';
import type { CustomerAddress, Customer, Maybe } from '../types/front.graphql';

export const customerStoreActions = {
    // ========================================================================
    // STATE GETTERS
    // ========================================================================

    /** Get currentCustomer - matches mobile's customerProfile$.currentCustomer */
    getCurrentCustomer: (): Customer | undefined => customerStore$.currentCustomer.peek(),

    getAddresses: (): CustomerAddress[] => customerStore$.addresses.peek() ?? [],

    getDefaultShippingAddressId: (): Maybe<number> | undefined => customerStore$.defaultShippingAddressId.peek(),

    getDefaultBillingAddressId: (): Maybe<number> | undefined => customerStore$.defaultBillingAddressId.peek(),

    getSelectedAddressId: (): Maybe<number> | undefined => customerStore$.selectedAddressId.peek(),

    /** Get defaultOrFirstAddress - matches mobile's customerProfile$.defaultOrFirstAddress */
    getDefaultOrFirstAddress: (): CustomerAddress | undefined => customerStore$.defaultOrFirstAddress.peek(),

    getIsLoading: (): boolean => customerStore$.isLoading.peek() ?? false,

    getError: (): string | undefined => customerStore$.error.peek(),

    getState: () => ({
        currentCustomer: customerStore$.currentCustomer.peek(),
        addresses: customerStore$.addresses.peek() ?? [],
        defaultShippingAddressId: customerStore$.defaultShippingAddressId.peek(),
        defaultBillingAddressId: customerStore$.defaultBillingAddressId.peek(),
        selectedAddressId: customerStore$.selectedAddressId.peek(),
        defaultOrFirstAddress: customerStore$.defaultOrFirstAddress.peek(),
        isLoading: customerStore$.isLoading.peek() ?? false,
        error: customerStore$.error.peek(),
    }),

    // ========================================================================
    // MUTATIONS
    // ========================================================================

    /** Set currentCustomer - matches mobile's customerProfile$.currentCustomer.set() */
    setCurrentCustomer: (customer: Customer | undefined): void => {
        console.log('[CustomerStoreActions] Setting currentCustomer:', customer?.email);
        customerStore$.currentCustomer.set(customer);
    },

    setAddresses: (addresses: CustomerAddress[]): void => {
        console.log('[CustomerStoreActions] Setting addresses:', addresses.length);
        customerStore$.addresses.set(addresses);

        // Set default addresses
        const defaultShipping = addresses.find(a => a.default_shipping);
        const defaultBilling = addresses.find(a => a.default_billing);

        if (defaultShipping?.id) {
            customerStore$.defaultShippingAddressId.set(defaultShipping.id);
        }
        if (defaultBilling?.id) {
            customerStore$.defaultBillingAddressId.set(defaultBilling.id);
        }
    },

    addAddress: (address: CustomerAddress): void => {
        console.log('[CustomerStoreActions] Adding address:', address.id);
        const currentAddresses = customerStore$.addresses.peek() ?? [];
        customerStore$.addresses.set([...currentAddresses, address]);

        if (address.default_shipping && address.id) {
            customerStore$.defaultShippingAddressId.set(address.id);
        }
        if (address.default_billing && address.id) {
            customerStore$.defaultBillingAddressId.set(address.id);
        }
    },

    updateAddress: (address: CustomerAddress): void => {
        console.log('[CustomerStoreActions] Updating address:', address.id);
        const currentAddresses = customerStore$.addresses.peek() ?? [];
        customerStore$.addresses.set(currentAddresses.map(a => (a.id === address.id ? address : a)));

        if (address.default_shipping && address.id) {
            customerStore$.defaultShippingAddressId.set(address.id);
        }
        if (address.default_billing && address.id) {
            customerStore$.defaultBillingAddressId.set(address.id);
        }
    },

    removeAddress: (addressId: Maybe<number>): void => {
        console.log('[CustomerStoreActions] Removing address:', addressId);
        const currentAddresses = customerStore$.addresses.peek() ?? [];
        customerStore$.addresses.set(currentAddresses.filter(a => a.id !== addressId));

        // Clear default if removed
        if (customerStore$.defaultShippingAddressId.peek() === addressId) {
            customerStore$.defaultShippingAddressId.set(undefined);
        }
        if (customerStore$.defaultBillingAddressId.peek() === addressId) {
            customerStore$.defaultBillingAddressId.set(undefined);
        }
        if (customerStore$.selectedAddressId.peek() === addressId) {
            customerStore$.selectedAddressId.set(undefined);
        }
    },

    setSelectedAddressId: (addressId: Maybe<number> | undefined): void => {
        console.log('[CustomerStoreActions] Setting selected address:', addressId);
        customerStore$.selectedAddressId.set(addressId);
    },

    /** Set defaultOrFirstAddress - matches mobile's customerProfile$.defaultOrFirstAddress.set() */
    setDefaultOrFirstAddress: (address: CustomerAddress | undefined): void => {
        console.log('[CustomerStoreActions] Setting defaultOrFirstAddress:', address?.id);
        customerStore$.defaultOrFirstAddress.set(address);
    },

    setLoading: (isLoading: boolean): void => {
        customerStore$.isLoading.set(isLoading);
    },

    setError: (error: string | undefined): void => {
        customerStore$.error.set(error);
    },

    resetStore: (): void => {
        console.log('[CustomerStoreActions] Resetting store');
        // Set each property individually so Legend-State detects changes
        // and syncs them to IndexedDB properly.
        customerStore$.currentCustomer.set(undefined);
        customerStore$.addresses.set([]);
        customerStore$.defaultShippingAddressId.set(undefined);
        customerStore$.defaultBillingAddressId.set(undefined);
        customerStore$.selectedAddressId.set(undefined);
        customerStore$.defaultOrFirstAddress.set(undefined);
        customerStore$.isLoading.set(false);
        customerStore$.error.set(undefined);
    },
} as const;

export type CustomerStoreActions = typeof customerStoreActions;
